import { createAsyncThunk } from "@reduxjs/toolkit";
import API from "../../../Api/Api";
import { ENDPOINTS } from "../../../Api/Endpoints";

const getError = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.response?.data?.msg ||
  error?.message ||
  fallback;

// Helper: PUT a single file to S3 using a presigned URL
const putFileToPresignedUrl = async (uploadUrl, file) => {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file?.type || "application/octet-stream",
    },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      `Failed to upload file to S3 (status ${response.status}). ${text || ""}`.trim()
    );
  }
};

export const uploadPrescriptionsAndReports = createAsyncThunk(
  "patientFlow/uploadPrescriptionsAndReports",
  async ({ prescriptionFiles = [], reportFiles = [] }, { rejectWithValue }) => {
    try {
      const allFiles = [];

      prescriptionFiles.forEach((file) => {
        if (file) {
          allFiles.push({
            type: "prescription",
            filename: file.name || "prescription.pdf",
            file,
          });
        }
      });

      reportFiles.forEach((file) => {
        if (file) {
          allFiles.push({
            type: "report",
            filename: file.name || "report.pdf",
            file,
          });
        }
      });

      if (!allFiles.length) {
        throw new Error("No files selected");
      }

      // 1) Ask backend for presigned PUT URLs
      const { data: uploadMeta } = await API.post(
        ENDPOINTS.PRESCRIPTION_UPLOAD_URLS,
        {
          files: allFiles.map((f) => ({
            type: f.type,
            filename: f.filename,
          })),
        }
      );

      const uploads = uploadMeta?.uploads || [];
      if (!uploads.length || uploads.length !== allFiles.length) {
        throw new Error("Invalid upload metadata from server");
      }

      // 2) Upload each file directly to S3 using its presigned URL
      await Promise.all(
        uploads.map((u, index) =>
          putFileToPresignedUrl(u.uploadUrl, allFiles[index].file)
        )
      );

      // 3) Tell backend to create Prescription documents from the S3 keys
      const items = uploads.map((u, index) => ({
        type: u.type,
        key: u.key,
        originalName: allFiles[index].file?.name || allFiles[index].filename,
      }));

      const { data: result } = await API.post(ENDPOINTS.UPLOAD_PRESCRIPTIONS, {
        items,
      });

      return result;
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to upload prescriptions"));
    }
  }
);

export const getMyPrescriptions = createAsyncThunk(
  "patientFlow/getMyPrescriptions",
  async (_, { rejectWithValue }) => {
    try {
      const res = await API.get(ENDPOINTS.GET_MY_PRESCRIPTIONS);
      return res.data;
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to fetch prescriptions"));
    }
  }
);

export const getSelections = createAsyncThunk(
  "patientFlow/getSelections",
  async (_, { rejectWithValue }) => {
    try {
      const res = await API.get(ENDPOINTS.GET_SELECTIONS);
      return res.data;
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to fetch selections"));
    }
  }
);

export const getLowestPrice = createAsyncThunk(
  "patientFlow/getLowestPrice",
  async ({ subCategoryId }, { rejectWithValue }) => {
    try {
      const res = await API.get(ENDPOINTS.GET_LOWEST_PRICE, {
        params: { subCategoryId },
      });

      return {
        ...res.data,
        requestedSubCategoryId: subCategoryId,
      };
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to fetch price range"));
    }
  }
);

export const getPreviewProviders = createAsyncThunk(
  "patientFlow/getPreviewProviders",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await API.post(ENDPOINTS.GET_PREVIEW, payload);
      return res.data;
    } catch (error) {
      return rejectWithValue(
        getError(error, "Failed to fetch preview providers")
      );
    }
  }
);

export const savePatientLocation = createAsyncThunk(
  "patientFlow/savePatientLocation",
  async ({ latitude, longitude }, { rejectWithValue }) => {
    try {
      const res = await API.post(ENDPOINTS.SAVE_PATIENT_LOCATION, { latitude, longitude });
      return res.data;
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to save location"));
    }
  }
);

export const createMatchRequest = createAsyncThunk(
  "patientFlow/createMatchRequest",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await API.post(ENDPOINTS.CREATE_MATCH_REQUEST, payload);
      return res.data;
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to create bid request"));
    }
  }
);

export const getProviderCount = createAsyncThunk(
  "patientFlow/getProviderCount",
  async (matchRequestId, { rejectWithValue }) => {
    try {
      const res = await API.get(ENDPOINTS.GET_PROVIDER_COUNT(matchRequestId));

      return {
        ...res.data,
        requestedMatchRequestId: matchRequestId,
      };
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to fetch provider count"));
    }
  }
);

export const getMatchResults = createAsyncThunk(
  "patientFlow/getMatchResults",
  async (matchRequestId, { rejectWithValue }) => {
    try {
      const res = await API.get(ENDPOINTS.GET_MATCH_RESULTS(matchRequestId));

      return {
        ...res.data,
        requestedMatchRequestId: matchRequestId,
      };
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to fetch match results"));
    }
  }
);

export const respondToBid = createAsyncThunk(
  "patientFlow/respondToBid",
  async ({ bidId, action }, { rejectWithValue }) => {
    try {
      const res = await API.post(ENDPOINTS.RESPOND_TO_BID(bidId), { action });

      return {
        ...res.data,
        bidId,
        action,
      };
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to respond to bid"));
    }
  }
);

export const patientCounterBid = createAsyncThunk(
  "patientFlow/patientCounterBid",
  async ({ bidId, amount }, { rejectWithValue }) => {
    try {
      const res = await API.post(ENDPOINTS.PATIENT_COUNTER_BID(bidId), { amount });
      return { ...res.data, bidId, amount };
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to counter bid"));
    }
  }
);

export const createConversation = createAsyncThunk(
  "patientFlow/createConversation",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await API.post(ENDPOINTS.CREATE_CONVERSATION, payload);
      return res.data;
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to create conversation"));
    }
  }
);

export const getConversationById = createAsyncThunk(
  "patientFlow/getConversationById",
  async (conversationId, { rejectWithValue }) => {
    try {
      const res = await API.get(ENDPOINTS.GET_CONVERSATION_BY_ID(conversationId));
      return res.data;
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to fetch conversation"));
    }
  }
);

export const getConversationMessages = createAsyncThunk(
  "patientFlow/getConversationMessages",
  async ({ conversationId, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const res = await API.get(
        ENDPOINTS.GET_CONVERSATION_MESSAGES(conversationId),
        {
          params: { page, limit },
        }
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to fetch messages"));
    }
  }
);

export const sendConversationMessage = createAsyncThunk(
  "patientFlow/sendConversationMessage",
  async ({ conversationId, text }, { rejectWithValue }) => {
    try {
      const res = await API.post(
        ENDPOINTS.SEND_CONVERSATION_MESSAGE(conversationId),
        { text }
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to send message"));
    }
  }
);

export const bookAppointment = createAsyncThunk(
  "patientFlow/bookAppointment",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await API.post(ENDPOINTS.BOOK_APPOINTMENT, payload);
      return res.data;
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to book appointment"));
    }
  }
);

export const getMyAppointments = createAsyncThunk(
  "patientFlow/getMyAppointments",
  async (_, { rejectWithValue }) => {
    try {
      const res = await API.get(ENDPOINTS.GET_MY_APPOINTMENTS);
      return res.data;
    } catch (error) {
      return rejectWithValue(getError(error, "Failed to fetch appointments"));
    }
  }
);
